import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { showToast, Pagination } from '@aosenuma/ui';
import { getSupabase, invalidateCache, isSupabaseConfigured, useUser, useParcelasTecnicas } from '@aosenuma/lib-supabase';
import { tituloParcelaConTitular, type VParcelaTecnica } from '@aosenuma/lib-types';
// Helper compartido con CapturaPage paso 2. Actualmente vive en
// features/captura/ — candidato a moverse a apps/equipo/src/lib/ en un
// follow-up de cleanup, pero el cross-feature import no rompe nada hoy.
import {
  type VisitaNarrativa,
  NARRATIVA_VACIA,
  NARRATIVA_FIELD_MAX,
  coerceNarrativa,
  formatVisitaFechaFull,
  formatVisitaFechaNodo,
  localDatetimeNow,
  narrativaPreview,
  narrativaTieneContenido,
} from '../captura/visitaNarrativa';

/**
 * Visita de campo persistida en `public.visitas` (migration 0034: narrativa jsonb).
 * - parcela + fecha requeridos.
 * - narrativa: 4 frentes de captura (descripcion_visita, descripcion_negociacion,
 *   reqs_area_legal, observaciones_tecnico). Al menos uno debe tener contenido.
 * - notas (text legacy): se rellena con auto-preview de la narrativa al guardar
 *   para compat con vistas/queries antiguos. Para visitas legacy con solo
 *   `notas`, el listado lee ese campo cuando narrativa es null.
 * - 2 inputs lat/lon manuales + botón "Capturar GPS".
 */
interface VisitaRecord {
  id: string;
  parcela_codigo: string;
  /** UUID interno de la parcela. Necesario para hacer UPDATE optimista
   *  en la edición (PostgREST no lo expone por el alias `parcelas:parcela_id`). */
  parcela_uuid: string | null;
  fecha_visita: string;
  notas: string | null;
  narrativa: VisitaNarrativa | null;
  lng: number | null;
  lat: number | null;
  /** Para la ventana de edición de 24h: el creador puede editar su propia
   *  visita en las primeras 24h. Mig 0075 garantiza esto a nivel RLS. */
  visitante_id: string | null;
  created_at: string;
}

/** Ventana de edición en milisegundos. Mismo valor que la RLS policy
 *  `visitas_creator_update_24h` en supabase/migrations/0075. */
const VISITA_EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * ¿Puede `userId` editar la visita `v` ahora?
 *  - debe ser el creador (visitante_id === userId)
 *  - dentro de 24h desde created_at
 * Si no se cumple alguna, devuelve un motivo legible para tooltip.
 * La RLS server-side es la fuente de verdad — esto solo controla la UI.
 */
function canEditVisita(v: VisitaRecord, userId: string | null | undefined):
  { ok: true; remainingMs: number } | { ok: false; reason: string } {
  if (!userId) return { ok: false, reason: 'No autenticado.' };
  if (!v.visitante_id) return { ok: false, reason: 'Visita sin creador registrado.' };
  if (v.visitante_id !== userId) return { ok: false, reason: 'Solo el creador puede editar esta visita.' };
  const remainingMs = VISITA_EDIT_WINDOW_MS - (Date.now() - new Date(v.created_at).getTime());
  if (remainingMs <= 0) return { ok: false, reason: 'Ventana de edición cerrada (24h).' };
  return { ok: true, remainingMs };
}

/** Formatea ms restantes como "Xh Ym" o "Xm" si <1h. Para el tooltip del
 *  botón "Editar". */
function formatEditWindowRemaining(ms: number): string {
  const totalMin = Math.floor(ms / 60000);
  if (totalMin <= 0) return '<1m';
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

type GpsPermState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unavailable';

function parseNumberInput(s: string): number | null {
  const v = s.trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function toEwktPoint(lng: number, lat: number): string {
  return `SRID=4326;POINT(${lng} ${lat})`;
}

function extractLngLat(geom: unknown): { lng: number | null; lat: number | null } {
  if (!geom) return { lng: null, lat: null };
  if (typeof geom === 'object' && geom !== null && 'coordinates' in geom) {
    const c = (geom as { coordinates?: unknown }).coordinates;
    if (Array.isArray(c) && c.length >= 2 && typeof c[0] === 'number' && typeof c[1] === 'number') {
      return { lng: c[0], lat: c[1] };
    }
  }
  return { lng: null, lat: null };
}

/** Normaliza string para búsqueda accent-insensitive (NFD + lowercase).
 *  Match "javier" ↔ "JÁVIER" y similares. */
function normSearch(s: string | null | undefined): string {
  if (!s) return '';
  return s.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
}

/** Filtro substring case+accent-insensitive. Cubre: ID público, num_parcela,
 *  nom_nucleo (ejido), municipio, titular_masked. CURP/INE/RFC no se buscan
 *  aquí — son PII cifrada que requeriría descifra batch con MFA. */
function matchParcela(p: VParcelaTecnica, q: string): boolean {
  if (!q) return true;
  const needle = normSearch(q);
  if (normSearch(p.parcela_id).includes(needle)) return true;
  if (p.num_parcela != null && String(p.num_parcela).includes(q.replace(/\D/g, ''))) return true;
  if (normSearch(p.nom_nucleo).includes(needle)) return true;
  if (normSearch(p.municipio).includes(needle)) return true;
  if (normSearch(p.titular_masked).includes(needle)) return true;
  return false;
}

/** Detecta el estado del permiso de geolocalización sin disparar el prompt. */
async function queryGpsPermission(): Promise<GpsPermState> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return 'unavailable';
  // Permissions API está en Safari 16+, Chrome, Firefox. Si no, fallback a 'unknown'.
  const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
  if (!perms?.query) return 'unknown';
  try {
    const status = await perms.query({ name: 'geolocation' as PermissionName });
    return (status.state as GpsPermState) ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Estilo compartido de los botones de acción dentro de cada card de visita
 *  (mismo look que el botón "+ detalle" original). */
const cardActionBtnStyle: React.CSSProperties = {
  padding: '2px 8px',
  background: 'transparent',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-mid)',
  cursor: 'pointer',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

/** Variante destructiva (borrar). Mismo borde, texto en rojo. */
const cardActionBtnDangerStyle: React.CSSProperties = {
  ...cardActionBtnStyle,
  color: 'var(--st-rojo)',
};

/** Tamaño de página de "Visitas recientes" y de la vista filtrada por parcela.
 *  Paginación server-side (.range() + count) — espejo de BitacoraPage. */
const VISITAS_PAGE_SIZE = 20;

export function VisitasPage() {
  const { user } = useUser();
  const { data: parcelasIdx, loading: parcelasLoading } = useParcelasTecnicas();
  const [searchParams, setSearchParams] = useSearchParams();
  const parcelaIdParamConsumedRef = useRef<string | null>(null);

  // Form state
  const [parcelaInput, setParcelaInput] = useState('');
  const [selectedParcela, setSelectedParcela] = useState<VParcelaTecnica | null>(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeSuggest, setActiveSuggest] = useState(0);

  const [fecha, setFecha] = useState<string>(() => localDatetimeNow());
  const [narrativa, setNarrativa] = useState<VisitaNarrativa>(() => ({ ...NARRATIVA_VACIA }));
  const [lat, setLat] = useState<string>('');
  const [lng, setLng] = useState<string>('');
  // Lista de visitas: row expandida muestra los 4 campos de narrativa read-only.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsPerm, setGpsPerm] = useState<GpsPermState>('unknown');

  const [submitBusy, setSubmitBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Lista persistida
  const [recent, setRecent] = useState<VisitaRecord[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Paginación server-side de "Visitas recientes": página actual + total de
  // filas (count: 'exact'). `recent` contiene solo la página visible.
  const [recientPage, setRecientPage] = useState(1);
  const [recientTotal, setRecientTotal] = useState(0);

  // Filtro "ver todas las visitas de esta parcela": reemplaza la lista de
  // recientes por las visitas de un parcela_id (fetch dedicado, paginado igual).
  // Se activa desde el detalle de cada card.
  const [filterParcelaId, setFilterParcelaId] = useState<string | null>(null);
  const [filteredVisitas, setFilteredVisitas] = useState<VisitaRecord[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterPage, setFilterPage] = useState(1);
  const [filterTotal, setFilterTotal] = useState(0);
  // Visita en proceso de borrado: deshabilita su botón mientras corre el DELETE.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  /** Visita actualmente en modal de edición (solo creador, ventana 24h).
   *  `null` = modal cerrado. Mig 0075 hace cumplir la regla a nivel RLS;
   *  el modal solo abre si `canEditVisita()` retorna ok. */
  const [editing, setEditing] = useState<VisitaRecord | null>(null);

  /** Historial de visitas solo de la parcela seleccionada (línea de tiempo + detalle). */
  type VisitaParcelaHistorial = {
    id: string;
    fecha_visita: string;
    notas: string | null;
    narrativa: VisitaNarrativa | null;
  };
  const [parcelVisitas, setParcelVisitas] = useState<VisitaParcelaHistorial[]>([]);
  const [parcelVisitasLoading, setParcelVisitasLoading] = useState(false);
  const [parcelVisitaExpandedId, setParcelVisitaExpandedId] = useState<string | null>(null);

  // Refs para click-outside del dropdown.
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Draft persistence: si el técnico cierra la pestaña por accidente o
  // Safari descarga la página por memoria, recupera su progreso al volver.
  // Se limpia en submit exitoso.
  // ─────────────────────────────────────────────────────────────────────────
  const DRAFT_KEY = 'aosenuma-draft:visitas-form';
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw) as {
        parcelaInput?: string; fecha?: string;
        // Forma nueva: narrativa estructurada
        narrativa?: Partial<VisitaNarrativa>;
        // Forma vieja: notas plana (drafts pre-migración 0034). Se migra a
        // narrativa.descripcion_visita en hidratación.
        notas?: string;
        lat?: string; lng?: string;
      };
      if (d.parcelaInput) setParcelaInput(d.parcelaInput);
      if (d.fecha) setFecha(d.fecha);
      if (d.narrativa && typeof d.narrativa === 'object') {
        setNarrativa((prev) => ({
          descripcion_visita: typeof d.narrativa?.descripcion_visita === 'string' ? d.narrativa.descripcion_visita : prev.descripcion_visita,
          descripcion_negociacion: typeof d.narrativa?.descripcion_negociacion === 'string' ? d.narrativa.descripcion_negociacion : prev.descripcion_negociacion,
          reqs_area_legal: typeof d.narrativa?.reqs_area_legal === 'string' ? d.narrativa.reqs_area_legal : prev.reqs_area_legal,
          observaciones_tecnico: typeof d.narrativa?.observaciones_tecnico === 'string' ? d.narrativa.observaciones_tecnico : prev.observaciones_tecnico,
        }));
      } else if (typeof d.notas === 'string' && d.notas.trim().length > 0) {
        // Migración back-compat: draft viejo con notas plana → narrativa.descripcion_visita
        setNarrativa((prev) => ({ ...prev, descripcion_visita: d.notas as string }));
      }
      if (d.lat != null) setLat(d.lat);
      if (d.lng != null) setLng(d.lng);
      if (d.parcelaInput || (d.narrativa && Object.keys(d.narrativa).length > 0) || d.notas) {
        showToast('Restauramos tu borrador anterior.', 3500);
      }
    } catch { /* draft corrupto, lo ignoramos */ }
  }, []);
  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ parcelaInput, fecha, narrativa, lat, lng }));
      } catch { /* QuotaExceeded — no fatal */ }
    }, 400);
    return () => window.clearTimeout(t);
  }, [parcelaInput, fecha, narrativa, lat, lng]);
  function clearDraft() {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
  }

  /** Resultados filtrados (memoized). Sin query → vacío para forzar busqueda
   *  (browse de 2374 ASC mostraría solo COR-* dejando HUM-* invisibles).
   *  Sin limit en cliente: ya están todos en memoria, render de N <button>
   *  nativos es <100ms en Safari. Scroll vertical resuelve el largo. */
  const suggestions = useMemo(() => {
    const list = parcelasIdx ?? [];
    const q = parcelaInput.trim();
    if (!q) return [] as VParcelaTecnica[];
    const out: VParcelaTecnica[] = [];
    for (const p of list) {
      if (matchParcela(p, q)) out.push(p);
    }
    return out;
  }, [parcelasIdx, parcelaInput]);

  // Click outside cierra el dropdown.
  useEffect(() => {
    if (!showSuggest) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setShowSuggest(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showSuggest]);

  // Detecta permiso GPS al montar + listener de cambio (cuando user da
  // permiso desde Settings, Safari/Chrome notifican via PermissionStatus.onchange
  // y podemos disparar getCurrentPosition automaticamente sin que tenga que
  // recargar la pagina).
  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;

    const onChange = () => {
      if (cancelled || !status) return;
      const newState = (status.state as GpsPermState) ?? 'unknown';
      setGpsPerm(newState);
      if (newState === 'granted') {
        // Auto-captura cuando recien se concede el permiso.
        void captureGpsRaw();
      }
    };

    (async () => {
      const initial = await queryGpsPermission();
      if (cancelled) return;
      setGpsPerm(initial);

      // Suscribir cambios de permiso si Permissions API esta disponible.
      const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
      if (perms?.query) {
        try {
          status = await perms.query({ name: 'geolocation' as PermissionName });
          status.addEventListener('change', onChange);
        } catch { /* ignore */ }
      }
    })();

    return () => {
      cancelled = true;
      if (status) status.removeEventListener('change', onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Llama a getCurrentPosition sin volver a chequear permission (usado por
   *  el botón y por el auto-retry del onchange listener). */
  function captureGpsRaw() {
    if (!('geolocation' in navigator)) {
      setGpsPerm('unavailable');
      return;
    }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLng(pos.coords.longitude.toFixed(6));
        setLat(pos.coords.latitude.toFixed(6));
        setGpsBusy(false);
        setGpsPerm('granted');
        showToast(`GPS: ${pos.coords.longitude.toFixed(5)}, ${pos.coords.latitude.toFixed(5)}`);
      },
      (err) => {
        setGpsBusy(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGpsPerm('denied');
          showToast('Permiso de ubicación denegado.', 4500);
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          showToast('Ubicación no disponible. Ingresa lat/lng manualmente.', 5000);
        } else if (err.code === err.TIMEOUT) {
          showToast('GPS lento. Intenta de nuevo o ingresa manualmente.', 5000);
        } else {
          showToast(`GPS error: ${err.message}`, 5000);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  const fetchRecent = useCallback(async (page: number) => {
    if (!isSupabaseConfigured()) {
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    try {
      const sb = getSupabase();
      const from = (page - 1) * VISITAS_PAGE_SIZE;
      const to = from + VISITAS_PAGE_SIZE - 1;
      const { data, error, count } = await sb
        .from('visitas')
        .select('id, parcela_id, fecha_visita, notas, narrativa, geom_punto, visitante_id, created_at, parcelas:parcela_id(parcela_id)', { count: 'exact' })
        .order('fecha_visita', { ascending: false })
        .range(from, to);
      if (error) throw error;
      type Row = {
        id: string;
        parcela_id: string;
        fecha_visita: string;
        notas: string | null;
        narrativa: unknown;
        geom_punto: unknown;
        visitante_id: string | null;
        created_at: string;
        parcelas: { parcela_id: string } | { parcela_id: string }[] | null;
      };
      const rows = ((data ?? []) as unknown as Row[]).map((r) => {
        const par = Array.isArray(r.parcelas) ? r.parcelas[0] : r.parcelas;
        const { lng, lat } = extractLngLat(r.geom_punto);
        return {
          id: r.id,
          parcela_codigo: par?.parcela_id ?? '—',
          parcela_uuid: r.parcela_id,
          fecha_visita: r.fecha_visita,
          notas: r.notas,
          narrativa: r.narrativa == null ? null : coerceNarrativa(r.narrativa),
          lng,
          lat,
          visitante_id: r.visitante_id,
          created_at: r.created_at,
        } as VisitaRecord;
      });
      setRecent(rows);
      setRecientTotal(count ?? 0);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Error desconocido');
      setRecent([]);
      setRecientTotal(0);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { void fetchRecent(recientPage); }, [fetchRecent, recientPage]);

  const fetchVisitasForParcela = useCallback(async (codigoPublico: string) => {
    if (!isSupabaseConfigured()) {
      setParcelVisitas([]);
      setParcelVisitasLoading(false);
      return;
    }
    setParcelVisitasLoading(true);
    try {
      const sb = getSupabase();
      const { data: parRow } = await sb
        .from('parcelas')
        .select('id')
        .eq('parcela_id', codigoPublico)
        .maybeSingle();
      if (!parRow?.id) {
        setParcelVisitas([]);
        return;
      }
      const { data, error } = await sb
        .from('visitas')
        .select('id, fecha_visita, notas, narrativa')
        .eq('parcela_id', parRow.id)
        .order('fecha_visita', { ascending: false })
        .limit(50);
      if (error) throw error;
      type Row = { id: string; fecha_visita: string; notas: string | null; narrativa: unknown };
      setParcelVisitas(
        ((data ?? []) as Row[]).map((r) => ({
          id: r.id,
          fecha_visita: r.fecha_visita,
          notas: r.notas,
          narrativa: r.narrativa == null ? null : coerceNarrativa(r.narrativa),
        })),
      );
    } catch {
      setParcelVisitas([]);
    } finally {
      setParcelVisitasLoading(false);
    }
  }, []);

  /** Trae las visitas de una parcela (paginadas, .range()+count) y las muestra
   *  en lugar de la lista de recientes. Disparado desde el botón "ver todas"
   *  del detalle de cada card. */
  const fetchVisitasFiltered = useCallback(async (codigoPublico: string, page: number) => {
    if (!isSupabaseConfigured()) return;
    setFilterParcelaId(codigoPublico);
    setFilterLoading(true);
    try {
      const sb = getSupabase();
      const { data: parRow } = await sb
        .from('parcelas')
        .select('id')
        .eq('parcela_id', codigoPublico)
        .maybeSingle();
      if (!parRow?.id) {
        setFilteredVisitas([]);
        setFilterTotal(0);
        return;
      }
      const from = (page - 1) * VISITAS_PAGE_SIZE;
      const to = from + VISITAS_PAGE_SIZE - 1;
      const { data, error, count } = await sb
        .from('visitas')
        .select('id, fecha_visita, notas, narrativa, geom_punto, visitante_id, created_at', { count: 'exact' })
        .eq('parcela_id', parRow.id)
        .order('fecha_visita', { ascending: false })
        .range(from, to);
      if (error) throw error;
      type Row = {
        id: string; fecha_visita: string; notas: string | null;
        narrativa: unknown; geom_punto: unknown;
        visitante_id: string | null; created_at: string;
      };
      setFilteredVisitas(
        ((data ?? []) as Row[]).map((r) => {
          const { lng, lat } = extractLngLat(r.geom_punto);
          return {
            id: r.id,
            parcela_codigo: codigoPublico,
            parcela_uuid: parRow.id,
            fecha_visita: r.fecha_visita,
            notas: r.notas,
            narrativa: r.narrativa == null ? null : coerceNarrativa(r.narrativa),
            lng,
            lat,
            visitante_id: r.visitante_id,
            created_at: r.created_at,
          } as VisitaRecord;
        }),
      );
      setFilterTotal(count ?? 0);
    } catch (e) {
      setFilteredVisitas([]);
      setFilterTotal(0);
      showToast(e instanceof Error ? e.message : 'No se pudieron cargar las visitas de la parcela.', 5000);
    } finally {
      setFilterLoading(false);
    }
  }, []);

  /** Borra una visita capturada (DELETE físico — RLS lo permite a staff
   *  aosenuma vía visitas_staff_write). Pide confirmación y refresca las
   *  listas afectadas. */
  async function onDeleteVisita(v: VisitaRecord) {
    const ok = window.confirm(
      `¿Borrar la visita de ${v.parcela_codigo} del ${formatVisitaFechaFull(v.fecha_visita)}? ` +
        'Esta acción no se puede deshacer.',
    );
    if (!ok) return;
    if (!isSupabaseConfigured()) {
      showToast('Supabase no configurado.', 4000);
      return;
    }
    setDeletingId(v.id);
    try {
      const sb = getSupabase();
      const { error } = await sb.from('visitas').delete().eq('id', v.id);
      if (error) throw error;
      showToast('Visita borrada.');
      if (expandedId === v.id) setExpandedId(null);
      // Refrescar recientes con clamp de página: si la página actual se queda
      // vacía tras el borrado, retrocede una. setRecientPage dispara el
      // useEffect; si la página no cambia, refetch explícito.
      const recientPages = Math.max(1, Math.ceil(Math.max(0, recientTotal - 1) / VISITAS_PAGE_SIZE));
      const recientTarget = Math.min(recientPage, recientPages);
      if (recientTarget !== recientPage) setRecientPage(recientTarget);
      else void fetchRecent(recientTarget);
      // Refrescar la vista filtrada si está activa (mismo clamp).
      if (filterParcelaId) {
        const filterPages = Math.max(1, Math.ceil(Math.max(0, filterTotal - 1) / VISITAS_PAGE_SIZE));
        const filterTarget = Math.min(filterPage, filterPages);
        if (filterTarget !== filterPage) setFilterPage(filterTarget);
        void fetchVisitasFiltered(filterParcelaId, filterTarget);
      }
      if (selectedParcela) void fetchVisitasForParcela(selectedParcela.parcela_id);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo borrar la visita.', 5000);
    } finally {
      setDeletingId(null);
    }
  }

  /** Guarda los cambios de edición de una visita. RLS (mig 0075) hace el
   *  guard de creador + ventana 24h; si la query no afecta filas, sabemos
   *  que la ventana se cerró o el usuario ya no es el creador. */
  async function onSaveEditedVisita(
    original: VisitaRecord,
    patch: {
      fecha_visita: string;
      notas: string | null;
      narrativa: VisitaNarrativa | null;
      geom_punto: string | null;  // EWKT 'SRID=4326;POINT(lng lat)' o null
    },
  ): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      showToast('Supabase no configurado.', 4000);
      return false;
    }
    try {
      const sb = getSupabase();
      // .select() después del UPDATE para detectar 0 filas (RLS denegó silenciosamente).
      const { data, error } = await sb
        .from('visitas')
        .update({
          fecha_visita: patch.fecha_visita,
          notas:        patch.notas,
          narrativa:    patch.narrativa,
          geom_punto:   patch.geom_punto,
        })
        .eq('id', original.id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        showToast(
          'No se pudo guardar: la ventana de edición (24h) expiró o ya no eres el creador.',
          6000,
        );
        return false;
      }
      showToast('Visita actualizada.');
      // Refrescos: mismo patrón que onDelete pero sin clamp de página.
      void fetchRecent(recientPage);
      if (filterParcelaId) void fetchVisitasFiltered(filterParcelaId, filterPage);
      if (selectedParcela) void fetchVisitasForParcela(selectedParcela.parcela_id);
      return true;
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo guardar la visita.', 5000);
      return false;
    }
  }

  useEffect(() => {
    setParcelVisitaExpandedId(null);
    if (!selectedParcela) {
      setParcelVisitas([]);
      setParcelVisitasLoading(false);
      return;
    }
    void fetchVisitasForParcela(selectedParcela.parcela_id);
  }, [selectedParcela, fetchVisitasForParcela]);

  const pickParcela = useCallback((p: VParcelaTecnica) => {
    setSelectedParcela(p);
    setParcelaInput(p.parcela_id);
    setShowSuggest(false);
    setActiveSuggest(0);
  }, []);

  /** Deep link desde Captura: `/visitas?parcela_id=HUM-…` precarga el selector. */
  useEffect(() => {
    const raw = searchParams.get('parcela_id');
    if (!raw?.trim()) {
      parcelaIdParamConsumedRef.current = null;
      return;
    }
    if (!parcelasIdx || parcelasLoading) return;

    const codigo = raw.trim().toUpperCase();
    if (parcelaIdParamConsumedRef.current === codigo) return;

    parcelaIdParamConsumedRef.current = codigo;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('parcela_id');
        return next;
      },
      { replace: true },
    );

    const p = parcelasIdx.find((x) => x.parcela_id.toUpperCase() === codigo);
    if (!p) {
      showToast(
        `Parcela «${codigo}» no está en tu inventario asignado. Busca el código manualmente.`,
        5500,
      );
      return;
    }
    pickParcela(p);
    showToast('Parcela lista. Completa y guarda la visita aquí.', 3500);
  }, [parcelasIdx, parcelasLoading, pickParcela, searchParams, setSearchParams]);

  function onParcelaInputChange(v: string) {
    setParcelaInput(v);
    setShowSuggest(true);
    setActiveSuggest(0);
    // Si el texto coincide exactamente con un código, auto-select sin click.
    const exact = (parcelasIdx ?? []).find((p) => p.parcela_id.toLowerCase() === v.trim().toLowerCase());
    setSelectedParcela(exact ?? null);
  }

  function onParcelaKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showSuggest && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setShowSuggest(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggest((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggest((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const pick = suggestions[activeSuggest];
      if (pick) {
        e.preventDefault();
        pickParcela(pick);
      }
    } else if (e.key === 'Escape') {
      setShowSuggest(false);
    }
  }

  async function captureGps() {
    if (!('geolocation' in navigator)) {
      setGpsPerm('unavailable');
      showToast('Este navegador no soporta geolocalización.', 4000);
      return;
    }
    // Releer permiso por si cambió desde el último mount.
    const fresh = await queryGpsPermission();
    setGpsPerm(fresh);
    if (fresh === 'denied') {
      // Safari/Chrome NO vuelven a mostrar prompt si ya negaste — es por
      // privacidad. Solo el user puede resetearlo desde Settings del sitio.
      showToast(
        'Permiso bloqueado. Cambia el ajuste de Ubicación del sitio y recarga, o ingresa lat/lng manual.',
        6000,
      );
      return;
    }
    captureGpsRaw();
  }

  async function onSubmit() {
    setFormError(null);

    const codigo = parcelaInput.trim().toUpperCase();
    if (!codigo) { setFormError('Falta el código de parcela.'); return; }
    // Validamos que haya match en el indice (evita typos que no existen).
    const matched = (parcelasIdx ?? []).find((p) => p.parcela_id.toUpperCase() === codigo);
    if (!matched) {
      setFormError(`Parcela "${codigo}" no existe en el inventario. Usa el autocomplete.`);
      return;
    }
    if (!fecha) { setFormError('Falta fecha y hora de la visita.'); return; }
    if (!narrativaTieneContenido(narrativa)) {
      setFormError('Captura al menos uno de los 4 campos de narrativa.');
      return;
    }

    const lngN = parseNumberInput(lng);
    const latN = parseNumberInput(lat);
    if ((lngN === null) !== (latN === null)) {
      setFormError('Captura ambas coordenadas o déjalas vacías.');
      return;
    }
    if (lngN !== null && (lngN < -180 || lngN > 180)) {
      setFormError('Longitud fuera de rango (-180 a 180).'); return;
    }
    if (latN !== null && (latN < -90 || latN > 90)) {
      setFormError('Latitud fuera de rango (-90 a 90).'); return;
    }

    if (!isSupabaseConfigured()) {
      setFormError('Supabase no configurado.');
      return;
    }
    setSubmitBusy(true);
    try {
      const sb = getSupabase();
      const { data: parRow, error: parErr } = await sb
        .from('parcelas')
        .select('id')
        .eq('parcela_id', codigo)
        .maybeSingle();
      if (parErr) throw parErr;
      if (!parRow) {
        setFormError(`Parcela "${codigo}" no encontrada en DB.`);
        return;
      }

      // Resumen automático para columna `notas` (compat con vistas legacy y
      // queries que aún no leen `narrativa`). max 200 chars.
      const notasResumen = narrativaPreview(narrativa, 200) || null;
      const payload: Record<string, unknown> = {
        parcela_id: parRow.id,
        fecha_visita: new Date(fecha).toISOString(),
        narrativa,
        notas: notasResumen,
      };
      if (user?.id) payload.visitante_id = user.id;
      if (lngN !== null && latN !== null) {
        payload.geom_punto = toEwktPoint(lngN, latN);
      }

      const { error: insErr } = await sb.from('visitas').insert(payload);
      if (insErr) throw insErr;

      const parcelaCodigo = matched.parcela_id;
      setNarrativa({ ...NARRATIVA_VACIA });
      setLat(''); setLng('');
      setFecha(localDatetimeNow());
      clearDraft();
      invalidateCache(['parcelas_tecnicas']);
      showToast('Visita registrada.');
      // La visita nueva es la más reciente → vive en la página 1. Saltamos
      // allá; si ya estábamos en la 1, refetch explícito (setState no-op no
      // dispara el useEffect).
      if (recientPage === 1) void fetchRecent(1);
      else setRecientPage(1);
      void fetchVisitasForParcela(parcelaCodigo);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Error al registrar la visita.');
    } finally {
      setSubmitBusy(false);
    }
  }

  const showingFiltered = filterParcelaId !== null;
  const displayList = showingFiltered ? filteredVisitas : recent;
  const displayLoading = showingFiltered ? filterLoading : loadingList;
  const displayTotal = showingFiltered ? filterTotal : recientTotal;
  const recientTotalPags = Math.max(1, Math.ceil(recientTotal / VISITAS_PAGE_SIZE));
  const filterTotalPags = Math.max(1, Math.ceil(filterTotal / VISITAS_PAGE_SIZE));

  return (
    <section className="panel">
      <header className="panel-header">
        <h1>Visitas de campo</h1>
        <p className="panel-sub">Bitácora persistida con georreferencia y trazabilidad.</p>
      </header>
      <div className="panel-body">
        <div className="glass-panel" style={{ padding: '18px 22px', marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
            Registrar nueva visita
          </h3>
          <div className="form-row">
            <label style={{ position: 'relative' }}>
              <span>Parcela <em style={{ color: 'var(--st-rojo)', fontStyle: 'normal' }}>*</em></span>
              <input
                ref={inputRef}
                value={parcelaInput}
                onChange={(e) => onParcelaInputChange(e.target.value)}
                onFocus={() => setShowSuggest(true)}
                onKeyDown={onParcelaKey}
                placeholder={parcelasLoading ? 'Cargando inventario…' : 'HUM-P0042 / 0042 / municipio'}
                autoComplete="off"
                spellCheck={false}
                aria-autocomplete="list"
                aria-expanded={showSuggest}
                aria-controls="parcela-suggest"
              />
              {showSuggest && suggestions.length > 0 && (
                <div
                  ref={dropdownRef}
                  id="parcela-suggest"
                  role="listbox"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    marginTop: 4,
                    maxHeight: 360,
                    overflowY: 'auto',
                    background: 'var(--bg-mid)',
                    color: 'var(--text)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm, 6px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  }}
                >
                  {suggestions.map((p, i) => (
                    <button
                      key={p.parcela_id}
                      type="button"
                      role="option"
                      aria-selected={i === activeSuggest}
                      onMouseEnter={() => setActiveSuggest(i)}
                      onClick={() => pickParcela(p)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 12px',
                        border: 'none',
                        background: i === activeSuggest ? 'rgba(58, 156, 126, 0.14)' : 'transparent',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontFamily: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <strong style={{ fontFamily: 'var(--font-display, system-ui)' }}>
                        {tituloParcelaConTitular(p.parcela_id, p.num_parcela, p.titular_masked)}
                      </strong>
                      <span style={{ color: 'var(--text-dim)', marginLeft: 8, fontSize: 11 }}>
                        · {p.municipio}
                        {p.nom_nucleo ? ` · ${p.nom_nucleo}` : ''}
                        {p.area_ha ? ` · ${p.area_ha.toFixed(2)} ha` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {showSuggest && suggestions.length === 0 && (
                <div
                  style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    marginTop: 4, padding: '8px 12px',
                    background: 'var(--bg-mid)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm, 6px)',
                    fontSize: 12, color: 'var(--text-dim)',
                  }}
                >
                  {parcelaInput.trim()
                    ? `Sin coincidencias para "${parcelaInput}".`
                    : `Tipea para buscar entre ${(parcelasIdx ?? []).length} parcelas (HUM-/COR-/0042/Huimilpan…)`}
                </div>
              )}
            </label>
            <label>
              <span>Fecha y hora <em style={{ color: 'var(--st-rojo)', fontStyle: 'normal' }}>*</em></span>
              <input type="datetime-local" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
            </label>
          </div>

          {selectedParcela && (
            <div
              style={{
                margin: '8px 0 14px', padding: '10px 12px',
                background: 'rgba(58, 156, 126, 0.08)',
                border: '1px solid rgba(58, 156, 126, 0.25)',
                borderRadius: 'var(--radius-sm, 6px)',
                fontSize: 12,
              }}
              role="status"
              aria-live="polite"
            >
              <strong>
                {tituloParcelaConTitular(
                  selectedParcela.parcela_id,
                  selectedParcela.num_parcela,
                  selectedParcela.titular_masked,
                )}
              </strong>
              <span style={{ color: 'var(--text-dim)', marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>
                {selectedParcela.parcela_id}
              </span>
              <span style={{ color: 'var(--text-dim)', marginLeft: 8 }}>
                {selectedParcela.municipio}
                {selectedParcela.nom_nucleo ? ` · Núcleo ${selectedParcela.nom_nucleo}` : ''}
                {' · '}{selectedParcela.area_ha.toFixed(2)} ha
                {' · '}estado <em style={{ fontStyle: 'normal' }}>{selectedParcela.estado}</em>
              </span>
            </div>
          )}

          <div className="visitas-parcel-timeline-section">
            <h4 className="visitas-timeline-heading">Línea de tiempo · visitas de esta parcela</h4>
            {(() => {
              const timelineState: 'idle' | 'loading' | 'empty' | 'has-visits' =
                !selectedParcela ? 'idle'
                : parcelVisitasLoading ? 'loading'
                : parcelVisitas.length === 0 ? 'empty'
                : 'has-visits';
              return (
                <div
                  className={`visita-timeline visita-timeline--${timelineState}`}
                  role={timelineState === 'has-visits' ? 'list' : 'status'}
                  aria-label="Historial de visitas de la parcela seleccionada"
                >
                  {timelineState === 'idle' && (
                    <div className="visita-timeline-message">
                      <svg className="visita-timeline-icon" viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>Selecciona una parcela arriba para ver su historial en la línea.</span>
                    </div>
                  )}
                  {timelineState === 'loading' && (
                    <div className="visita-timeline-message">
                      <span className="visita-timeline-spinner" aria-hidden="true" />
                      <span>Cargando historial…</span>
                    </div>
                  )}
                  {timelineState === 'empty' && (
                    <div className="visita-timeline-message">
                      <svg className="visita-timeline-icon" viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <polyline points="12 7 12 12 15 14" />
                      </svg>
                      <div className="visita-timeline-message-text">
                        <strong>Aún no hay visitas registradas</strong>
                        <small>La próxima quedará marcada en la línea →</small>
                      </div>
                    </div>
                  )}
                  {timelineState === 'has-visits' &&
                    [...parcelVisitas].reverse().map((v, i, arr) => {
                      const isLatest = i === arr.length - 1;
                      const isExpanded = parcelVisitaExpandedId === v.id;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          role="listitem"
                          className={`visita-timeline-node${isLatest ? ' is-latest' : ''}${isExpanded ? ' is-active' : ''}`}
                          onClick={() => setParcelVisitaExpandedId(isExpanded ? null : v.id)}
                          title={formatVisitaFechaFull(v.fecha_visita)}
                          aria-pressed={isExpanded}
                        >
                          <span className="visita-timeline-dot" aria-hidden />
                          <span className="visita-timeline-label">{formatVisitaFechaNodo(v.fecha_visita)}</span>
                        </button>
                      );
                    })}
                  {timelineState !== 'idle' && timelineState !== 'loading' && (
                    <div className="visita-timeline-now" aria-label="Hoy">
                      <span className="visita-timeline-now-pulse" aria-hidden="true" />
                      <span className="visita-timeline-now-label">hoy</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {selectedParcela && parcelVisitas.length > 0 && (
              <div className="visitas-list visitas-list--parcel-context" aria-label="Detalle de visitas anteriores">
                <div className="visitas-list-head">
                  Visitas anteriores · {parcelVisitas.length}
                </div>
                <ul>
                  {parcelVisitas.map((v) => {
                    const expanded = parcelVisitaExpandedId === v.id;
                    const preview = narrativaPreview(v.narrativa) || v.notas?.slice(0, 80) || '';
                    return (
                      <li key={v.id} className={`visitas-list-item${expanded ? ' is-expanded' : ''}`}>
                        <button
                          type="button"
                          className="visitas-list-toggle"
                          onClick={() => setParcelVisitaExpandedId(expanded ? null : v.id)}
                          aria-expanded={expanded}
                        >
                          <span className="visitas-list-fecha">{formatVisitaFechaFull(v.fecha_visita)}</span>
                          <span className="visitas-list-preview">{preview || <em>(sin contenido)</em>}</span>
                          <span className="visitas-list-caret" aria-hidden>{expanded ? '−' : '+'}</span>
                        </button>
                        {expanded && v.narrativa && (
                          <dl className="visitas-list-detail">
                            <dt>Descripción general</dt>
                            <dd>{v.narrativa.descripcion_visita || <em>—</em>}</dd>
                            <dt>Negociación</dt>
                            <dd>{v.narrativa.descripcion_negociacion || <em>—</em>}</dd>
                            <dt>Área legal</dt>
                            <dd>{v.narrativa.reqs_area_legal || <em>—</em>}</dd>
                            <dt>Observaciones técnico</dt>
                            <dd>{v.narrativa.observaciones_tecnico || <em>—</em>}</dd>
                          </dl>
                        )}
                        {expanded && !v.narrativa && v.notas && (
                          <p className="visitas-list-detail-legacy">{v.notas}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Narrativa estructurada — 4 frentes (consistente con CapturaPage paso 2).
              Al menos uno debe tener contenido; el campo `notas` legacy se
              rellena con auto-preview en submit. */}
          <div className="form-row">
            <label className="full">
              <span>
                Descripción general de la visita <em style={{ color: 'var(--st-rojo)', fontStyle: 'normal' }}>*</em>
              </span>
              <textarea
                value={narrativa.descripcion_visita}
                onChange={(e) => setNarrativa((n) => ({ ...n, descripcion_visita: e.target.value }))}
                rows={3}
                maxLength={NARRATIVA_FIELD_MAX}
                placeholder="¿Quién recibió? ¿Qué sucedió en campo?"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="full">
              <span>Descripción de la negociación</span>
              <textarea
                value={narrativa.descripcion_negociacion}
                onChange={(e) => setNarrativa((n) => ({ ...n, descripcion_negociacion: e.target.value }))}
                rows={3}
                maxLength={NARRATIVA_FIELD_MAX}
                placeholder="Acuerdos, avances y puntos pendientes…"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="full">
              <span>Observaciones para el área legal</span>
              <textarea
                value={narrativa.reqs_area_legal}
                onChange={(e) => setNarrativa((n) => ({ ...n, reqs_area_legal: e.target.value }))}
                rows={2}
                maxLength={NARRATIVA_FIELD_MAX}
                placeholder="Solicitudes o alertas para el equipo legal…"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="full">
              <span>Observaciones del técnico</span>
              <textarea
                value={narrativa.observaciones_tecnico}
                onChange={(e) => setNarrativa((n) => ({ ...n, observaciones_tecnico: e.target.value }))}
                rows={2}
                maxLength={NARRATIVA_FIELD_MAX}
                placeholder="Notas internas, próximos pasos, etc."
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              <span>Longitud</span>
              <input
                type="number" step="0.000001" min={-180} max={180}
                value={lng} onChange={(e) => setLng(e.target.value)}
                placeholder="-100.291234" inputMode="decimal"
              />
            </label>
            <label>
              <span>Latitud</span>
              <input
                type="number" step="0.000001" min={-90} max={90}
                value={lat} onChange={(e) => setLat(e.target.value)}
                placeholder="20.379876" inputMode="decimal"
              />
            </label>
          </div>

          <div className="form-row" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => void captureGps()}
              disabled={gpsBusy || gpsPerm === 'unavailable'}
            >
              {gpsBusy ? 'Capturando GPS…' : 'Capturar GPS del navegador'}
            </button>
            {gpsPerm === 'denied' && (
              <div style={{ fontSize: 11, color: 'var(--st-rojo)', maxWidth: 480, lineHeight: 1.5 }}>
                <strong>Ubicación bloqueada por el navegador.</strong> Si necesitas el GPS:
                <ul style={{ margin: '4px 0 0 18px', padding: 0, color: 'var(--text-dim)' }}>
                  <li>
                    <strong>Safari (Mac):</strong> menú Safari → Settings → Websites →
                    Location → busca este sitio → cambiar a <strong>Allow</strong> → recarga.
                  </li>
                  <li>
                    <strong>Chrome:</strong> click en el candado izquierdo de la URL →
                    Site settings → Location → <strong>Allow</strong> → recarga.
                  </li>
                  <li>
                    Si no puedes activar GPS, ingresa lat/lng manualmente arriba —
                    el form acepta cualquier coordenada válida.
                  </li>
                </ul>
              </div>
            )}
            {gpsPerm === 'unavailable' && (
              <span style={{ fontSize: 11, color: 'var(--st-rojo)' }}>
                Este navegador no soporta geolocalización. Ingresa lat/lng manualmente.
              </span>
            )}
            {gpsPerm === 'prompt' && (
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                El navegador te pedirá autorización al pulsar el botón.
              </span>
            )}
            {gpsPerm === 'granted' && (
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                También puedes pegar coordenadas manualmente.
              </span>
            )}
          </div>

          {formError && (
            <div role="alert" style={{ color: 'var(--st-rojo)', marginTop: 10, fontSize: 12 }}>
              {formError}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={() => void onSubmit()} disabled={submitBusy}>
              {submitBusy ? 'Guardando…' : 'Guardar visita'}
            </button>
          </div>
        </div>

        <div className="glass-panel" style={{ padding: '18px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, margin: 0 }}>
              {showingFiltered ? (
                <>Visitas de <span style={{ color: 'var(--cream)' }}>{filterParcelaId}</span></>
              ) : (
                'Visitas recientes'
              )}{' '}
              {displayTotal > 0 && (
                <small style={{ color: 'var(--text-dim)', fontWeight: 400 }}>({displayTotal})</small>
              )}
            </h3>
            {showingFiltered && (
              <button
                type="button"
                onClick={() => {
                  setFilterParcelaId(null);
                  setFilteredVisitas([]);
                  setFilterPage(1);
                  setFilterTotal(0);
                }}
                style={{ ...cardActionBtnStyle, marginLeft: 'auto' }}
              >
                ← Ver todas las recientes
              </button>
            )}
          </div>
          {displayLoading ? (
            <p className="empty">Cargando…</p>
          ) : !showingFiltered && listError ? (
            <p className="empty" style={{ color: 'var(--st-rojo)' }}>Error: {listError}</p>
          ) : displayList.length === 0 ? (
            <p className="empty">
              {showingFiltered ? `Sin visitas para ${filterParcelaId}.` : 'Sin visitas registradas todavía.'}
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {displayList.map((v) => {
                const isExpanded = expandedId === v.id;
                const preview = narrativaPreview(v.narrativa, 140) || v.notas || '';
                const canExpand = v.narrativa != null;
                // Ventana de edición — solo creador, 24h. RLS hace cumplir
                // server-side; esto solo controla la affordance.
                const editCheck = canEditVisita(v, user?.id);
                return (
                  <li
                    key={v.id}
                    style={{
                      padding: '10px 12px', background: 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isExpanded ? 'rgba(74,156,165,0.5)' : 'var(--glass-border)'}`,
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 12,
                      transition: 'border-color 0.12s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ color: 'var(--cream)' }}>{v.parcela_codigo}</strong>
                      <span style={{ color: 'var(--text-dim)' }}>
                        {formatVisitaFechaFull(v.fecha_visita)}
                      </span>
                      {v.lng !== null && v.lat !== null && (
                        <span style={{ color: 'var(--text-dim)', fontFamily: 'monospace', fontSize: 11 }}>
                          · {v.lng.toFixed(5)}, {v.lat.toFixed(5)}
                        </span>
                      )}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                        {canExpand && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : v.id)}
                            style={cardActionBtnStyle}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? '− detalle' : '+ detalle'}
                          </button>
                        )}
                        {editCheck.ok && (
                          <button
                            type="button"
                            onClick={() => setEditing(v)}
                            style={cardActionBtnStyle}
                            title={`Editable por ${formatEditWindowRemaining(editCheck.remainingMs)} más`}
                          >
                            Editar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void onDeleteVisita(v)}
                          disabled={deletingId === v.id}
                          style={cardActionBtnDangerStyle}
                          title="Borrar esta visita"
                        >
                          {deletingId === v.id ? 'Borrando…' : 'Borrar'}
                        </button>
                      </div>
                    </div>
                    {preview && !isExpanded && (
                      <div style={{ color: 'var(--text-dim)', marginTop: 4, fontSize: 11, whiteSpace: 'pre-wrap' }}>
                        {preview}
                      </div>
                    )}
                    {isExpanded && v.narrativa && (
                      <dl
                        style={{
                          display: 'grid', gridTemplateColumns: 'max-content 1fr',
                          gap: '4px 12px', margin: '8px 0 0', fontSize: 11,
                        }}
                      >
                        <dt style={{ color: 'var(--text-dim)', textTransform: 'uppercase', fontSize: 9.5, letterSpacing: '0.08em', paddingTop: 2 }}>Descripción general</dt>
                        <dd style={{ margin: 0, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{v.narrativa.descripcion_visita || <em>—</em>}</dd>
                        <dt style={{ color: 'var(--text-dim)', textTransform: 'uppercase', fontSize: 9.5, letterSpacing: '0.08em', paddingTop: 2 }}>Negociación</dt>
                        <dd style={{ margin: 0, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{v.narrativa.descripcion_negociacion || <em>—</em>}</dd>
                        <dt style={{ color: 'var(--text-dim)', textTransform: 'uppercase', fontSize: 9.5, letterSpacing: '0.08em', paddingTop: 2 }}>Área legal</dt>
                        <dd style={{ margin: 0, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{v.narrativa.reqs_area_legal || <em>—</em>}</dd>
                        <dt style={{ color: 'var(--text-dim)', textTransform: 'uppercase', fontSize: 9.5, letterSpacing: '0.08em', paddingTop: 2 }}>Observaciones técnico</dt>
                        <dd style={{ margin: 0, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{v.narrativa.observaciones_tecnico || <em>—</em>}</dd>
                      </dl>
                    )}
                    {isExpanded && (
                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setFilterPage(1);
                            void fetchVisitasFiltered(v.parcela_codigo, 1);
                          }}
                          disabled={showingFiltered && filterParcelaId === v.parcela_codigo}
                          style={cardActionBtnStyle}
                        >
                          {showingFiltered && filterParcelaId === v.parcela_codigo
                            ? `Mostrando todas las de ${v.parcela_codigo}`
                            : `Ver todas las visitas de ${v.parcela_codigo}`}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {showingFiltered ? (
            <Pagination
              current={filterPage}
              total={filterTotalPags}
              onChange={(p) => {
                setFilterPage(p);
                if (filterParcelaId) void fetchVisitasFiltered(filterParcelaId, p);
              }}
              size="sm"
              compact
            />
          ) : (
            <Pagination
              current={recientPage}
              total={recientTotalPags}
              onChange={setRecientPage}
              size="sm"
              compact
            />
          )}
        </div>
      </div>
      {editing && (
        <EditVisitaModal
          visita={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            const ok = await onSaveEditedVisita(editing, patch);
            if (ok) setEditing(null);
          }}
        />
      )}
    </section>
  );
}

// =============================================================================
// EditVisitaModal — edición de una visita dentro de la ventana de 24h.
// Render: overlay fixed con form de los 4 frentes narrativos + fecha + coords.
// La RLS (mig 0075) hace el guard real; este componente solo es la UI.
// =============================================================================
function EditVisitaModal({
  visita,
  onClose,
  onSave,
}: {
  visita: VisitaRecord;
  onClose: () => void;
  onSave: (patch: {
    fecha_visita: string;
    notas: string | null;
    narrativa: VisitaNarrativa | null;
    geom_punto: string | null;
  }) => Promise<void>;
}) {
  // ISO → datetime-local. Trabajamos en tz local para que el técnico edite
  // con la misma fecha/hora que ve en la card.
  function isoToLocalDatetime(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  const [fecha, setFecha] = useState<string>(() => isoToLocalDatetime(visita.fecha_visita));
  const [narrativa, setNarrativa] = useState<VisitaNarrativa>(() =>
    visita.narrativa ? { ...visita.narrativa } : { ...NARRATIVA_VACIA },
  );
  const [latStr, setLatStr] = useState<string>(visita.lat != null ? String(visita.lat) : '');
  const [lngStr, setLngStr] = useState<string>(visita.lng != null ? String(visita.lng) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Atajo Esc cierra (consistente con doc-modal de Expedientes).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!narrativaTieneContenido(narrativa)) {
      setError('Al menos uno de los 4 campos narrativos debe tener contenido.');
      return;
    }
    const lat = parseNumberInput(latStr);
    const lng = parseNumberInput(lngStr);
    if ((latStr.trim() !== '' || lngStr.trim() !== '') && (lat == null || lng == null)) {
      setError('Si capturas coordenadas, lat y lng deben ser ambas numéricas.');
      return;
    }
    if (lat != null && (lat < -90 || lat > 90)) {
      setError('Latitud fuera de rango (-90..90).');
      return;
    }
    if (lng != null && (lng < -180 || lng > 180)) {
      setError('Longitud fuera de rango (-180..180).');
      return;
    }

    // notas legacy: regeneramos el auto-preview a partir de la narrativa
    // (mismo patrón que el insert original) para mantener consistencia con
    // queries antiguas que aún leen `notas`.
    const notasPreview = narrativaPreview(narrativa, 200);

    setSaving(true);
    try {
      await onSave({
        fecha_visita: new Date(fecha).toISOString(),
        notas: notasPreview || null,
        narrativa,
        geom_punto: lat != null && lng != null ? toEwktPoint(lng, lat) : null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(0,0,0,0.62)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="glass-panel"
        style={{
          width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto',
          padding: '20px 22px', borderRadius: 14,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, margin: 0 }}>
            Editar visita · <span style={{ color: 'var(--cream)' }}>{visita.parcela_codigo}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar (Esc)"
            title="Cerrar (Esc)"
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18, lineHeight: 1,
            }}
          >×</button>
        </header>

        <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '0 0 14px' }}>
          Solo el creador puede editar dentro de las 24h posteriores al guardado. Cambios quedan auditados.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--text-dim)' }}>Fecha y hora de la visita</span>
            <input
              type="datetime-local"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              required
              style={{ padding: '6px 8px', fontSize: 13 }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--text-dim)' }}>Descripción general</span>
            <textarea
              value={narrativa.descripcion_visita}
              onChange={(e) => setNarrativa((n) => ({ ...n, descripcion_visita: e.target.value }))}
              rows={3}
              style={{ padding: '6px 8px', fontSize: 12, resize: 'vertical' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--text-dim)' }}>Negociación</span>
            <textarea
              value={narrativa.descripcion_negociacion}
              onChange={(e) => setNarrativa((n) => ({ ...n, descripcion_negociacion: e.target.value }))}
              rows={2}
              style={{ padding: '6px 8px', fontSize: 12, resize: 'vertical' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--text-dim)' }}>Área legal</span>
            <textarea
              value={narrativa.reqs_area_legal}
              onChange={(e) => setNarrativa((n) => ({ ...n, reqs_area_legal: e.target.value }))}
              rows={2}
              style={{ padding: '6px 8px', fontSize: 12, resize: 'vertical' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--text-dim)' }}>Observaciones técnico</span>
            <textarea
              value={narrativa.observaciones_tecnico}
              onChange={(e) => setNarrativa((n) => ({ ...n, observaciones_tecnico: e.target.value }))}
              rows={2}
              style={{ padding: '6px 8px', fontSize: 12, resize: 'vertical' }}
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: 'var(--text-dim)' }}>Latitud</span>
              <input
                type="text" inputMode="decimal"
                value={latStr} onChange={(e) => setLatStr(e.target.value)}
                placeholder="20.4123" style={{ padding: '6px 8px', fontSize: 12 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: 'var(--text-dim)' }}>Longitud</span>
              <input
                type="text" inputMode="decimal"
                value={lngStr} onChange={(e) => setLngStr(e.target.value)}
                placeholder="-100.3245" style={{ padding: '6px 8px', fontSize: 12 }}
              />
            </label>
          </div>
        </div>

        {error && (
          <div role="alert" style={{ marginTop: 12, color: 'var(--st-rojo)', fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{ ...cardActionBtnStyle, padding: '6px 14px' }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
            style={{ padding: '6px 16px' }}
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </div>
  );
}
